import { createContext, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { tokenApi, backend, collector } from "src/api";
import { paths } from "src/paths";
import { useLocation } from "react-router";

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["SET_LOADING"] = "SET_LOADING";
  ActionType["SET_ERROR"] = "SET_ERROR";
})(ActionType || (ActionType = {}));

const initialState = {
  isInitialized: false,
  error: undefined,
};

// 여러 API가 동시에 access token 만료 응답을 받아도 refresh 요청은 한 번만 보낸다.
let refreshRequest = null;
const isCanceledRequest = (error) => axios.isCancel(error) || error?.code === "ERR_CANCELED";

const handlers = {
  INITIALIZE: (state, action) => {
    return {
      ...state,
      isInitialized: true,
    };
  },
  SET_ERROR: (state, action) => {
    const { error } = action.payload;

    return {
      ...state,
      error,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

/** axios config에 저장한 요청 시작 시각으로 응답 소요 시간을 ms 단위로 계산한다. */
const getDurationMs = (config) => {
  if (!config?._requestStartedAt) return "-";
  const now = typeof performance !== "undefined" ? performance.now() : Date.now();
  return `${Math.round(now - config._requestStartedAt)}ms`;
};

export const RequestContext = createContext({
  ...initialState,
  setLoading: () => Promise.resolve(),
});

export const RequestProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();

  const setError = useCallback(
    async (error) => {
      dispatch({
        type: ActionType.SET_ERROR,
        payload: {
          error,
        },
      });
    },
    [dispatch]
  );

  const refresh = async (dont) => {
    if (!refreshRequest) {
      refreshRequest = (async () => {
        const refreshToken = tokenApi.getRefreshToken();
        const response = await backend.post(
          "/oauth/refresh",
          { refreshToken },
          { _dont: dont }
        );
        const tokens = response.data;
        tokenApi.setAccessToken(tokens.accessToken);
        tokenApi.setRefreshToken(tokens.refreshToken);
      })().finally(() => {
        refreshRequest = null;
      });
    }
    return refreshRequest;
  };

  const responseHandler = useCallback(
    async (response) => {
      if (!response.config.url?.startsWith("/admin/system")) {
        console.log(
          "[%s]-[%s] %s : %O",
          response.config.url,
          response.config.method,
          getDurationMs(response.config),
          response.data
        );
      }
      // await setLoading(false);
      if (state.error) {
        await setError(undefined);
      }
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setError]
  );

  const responseErrorHandler = useCallback(
    async (err, instance) => {
      // Superseded/unmounted map requests reject normally, but are not server failures.
      if (isCanceledRequest(err)) return Promise.reject(err);
      const config = err.config;
      const duration = getDurationMs(config);
      if (err.response) {
        // Access Token was, expired
        const status = err.response.status;
        const data = err.response.data;
        if (status === 401 && !config._retry && data.code === "AU002") {
          console.log(
            "retry config: %O, status: %O, duration: %s, data: %O",
            config,
            status,
            duration,
            data
          );
          config._retry = true;
          try {
            await refresh(config._dont);
            return instance(config);
          } catch (refreshErr) {
            if (isCanceledRequest(refreshErr)) return Promise.reject(refreshErr);
            tokenApi.clearTokens();
            const searchParams = new URLSearchParams({
              returnTo: window.location.href,
            }).toString();
            window.location.href = paths.auth.jwt.login + `?${searchParams}`;
            return Promise.reject(refreshErr);
          }
        } else if (status === 401 && data.code === "AU003") {
          return Promise.reject(err);
        } else if (status === 401 && !config._dont) {
          // Check if user already logged out manually (token already cleared)
          const hasToken = tokenApi.getAccessToken();
          if (hasToken) {
            tokenApi.clearTokens();
            const searchParams = new URLSearchParams({
              returnTo: window.location.href,
            }).toString();
            window.location.href = paths.auth.jwt.login + `?${searchParams}`;
          }
          return Promise.reject(err);
        }

        if (!config._suppressGlobalError) {
          await setError(err);
        }
        return Promise.reject(err.response.data);
      }

      await setError(err);
      return Promise.reject(err);
    },
    [setError]
  );

  const backendErrorHandler = useCallback(
    async (err) => {
      return await responseErrorHandler(err, backend);
    },
    [responseErrorHandler]
  );

  const collectorErrorHandler = useCallback(
    async (err) => {
      return await responseErrorHandler(err, collector);
    },
    [responseErrorHandler]
  );

  const requestHandler = useCallback(
    async (config) => {
      if (state.error) {
        await setError(undefined);
      }
      const token = tokenApi.getAccessToken();
      if (token) {
        config.headers["Authorization"] = token;
      }
      config.headers["Content-Type"] = "application/json";
      config.headers["Accept"] = "application/json";
      config.headers["X-Page-Path"] = window.location.pathname;
      config._requestStartedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      return config;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setError]
  );

  const requestErrorHandler = useCallback(
    async (err) => {
      if (isCanceledRequest(err)) return Promise.reject(err);
      await setError(err);
      console.log("request error: %O", err);
      return Promise.reject(err);
    },
    [setError]
  );

  useEffect(() => {
    if (state.error) {
      setError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    // Prevent redirect on auth initialize
    // dont need to redirect to login for auth initialize
    const backendResponse = backend.interceptors.response.use(
      responseHandler,
      backendErrorHandler
    );
    const backendRequest = backend.interceptors.request.use(
      requestHandler,
      requestErrorHandler
    );
    const collectorResponse = collector.interceptors.response.use(
      responseHandler,
      collectorErrorHandler
    );
    const collectorRequest = collector.interceptors.request.use(
      requestHandler,
      requestErrorHandler
    );

    dispatch({
      type: ActionType.INITIALIZE,
      payload: {},
    });
    return () => {
      backend.interceptors.response.eject(backendResponse);
      backend.interceptors.request.eject(backendRequest);
      collector.interceptors.response.eject(collectorResponse);
      collector.interceptors.request.eject(collectorRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RequestContext.Provider
      value={{
        ...state,
      }}
    >
      {children}
    </RequestContext.Provider>
  );
};

RequestProvider.prototype = {
  children: PropTypes.node.isRequired,
};

export const RequestConsumer = RequestContext.Consumer;
